import { Button, Grid, Paper, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from "@mui/material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FilterComponent, NoRecordFound, PageLoader, Pagination, STableLoader } from "../../../shared_elements";
import { useEffect, useMemo, useState } from "react";
import { exportFleetClaimsApi, getAssetDropdowmListApi, getFleetClaimManagementListApi } from "../apiService";
import { checkApiStatus } from "../../../utils";
import { globalGetService } from "../../../utils/globalApiServices";
import ClaimManagementListing from "../components/ClaimManagementListing";
import { trackActivity } from "../../../utils/mixpanel";
import { enqueueSnackbar } from "notistack";
import { ButtonLoader } from "../../../shared_elements/components";
import { RoleBaseAccess } from "../../../hoc";
import DeploymentBannerWithOffset from "../../../shared_elements/components/DeploymentBannerWithOffSet";

const claimStatusOptions = [
	{ label: 'Submitted', value: 1 },
	{ label: 'In-Review', value: 2 },
    { label: 'Approved', value: 3 },
	{ label: 'Rejected', value: 4 },
	{ label: 'Disbursement Verification', value: 5 }, 
    { label: 'Paid', value: 6 },
];

const assetType = [
	{ label: 'Aircraft', value: 1 },
	{ label: 'Engine', value: 2 },
	{ label: 'APU', value: 3 },
	{ label: 'Landing Gear', value: 4 },
];


const FleetClaimManagementHd = ({ sortInfo, createSortHandler }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Claim Management`, page_type: page_type, })
    }
    const tableSorting = (key, name) => {
        return (
            <TableSortLabel
                active={sortInfo.sort_by === key ? true : false}
                direction={sortInfo.sort_by === key ? sortInfo.sort : 'asc'}
                onClick={() => { createSortHandler(key); handleItemClicked(`Table Sorting: ${name}`) }}
            >
                {name}
            </TableSortLabel>
        )
    }
    return (
        <TableHead>
            <TableRow>
                <TableCell className="table-actions-header" sx={{ minWidth: "60px" }}>Action(s)</TableCell>
                <TableCell sx={{ minWidth: "140px" }}>Assembly</TableCell>
                <TableCell sx={{ minWidth: "160px" }}> Event/Shop Visit</TableCell>
                <TableCell sx={{ minWidth: "140px" }}> {tableSorting('submission_date', 'Submission Date')}</TableCell>
                <TableCell sx={{ minWidth: "140px" }}> {tableSorting('shop_visit_cost', 'SV Cost')}</TableCell>
                <TableCell sx={{ minWidth: "140px" }}> {tableSorting('status_label', 'Claim Status')}</TableCell>
                <TableCell sx={{ minWidth: "140px" }}>Claim Amount</TableCell>
                <TableCell sx={{ minWidth: "140px" }}>Approved Amount</TableCell>
            </TableRow>
        </TableHead>
    )
}

const FleetClaimManagement = () => {
    const [skeletonLoader, setSkeletonLoader] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [sortInfo, setSortInfo] = useState({ sort: '', sort_by: '' });
    const [claimManagementData, setClaimManagementData] = useState();
    const [isExport, setExport] = useState();
    const [filter, setFilter] = useState({});
    const [assemblyOptions, setAssemblyOptions] = useState([]);
    const [drawerAssetType, setDrawerAssetType] = useState(undefined);

    useEffect(() => {
        setDrawerAssetType(undefined);
    }, [filter.asset_type]);

    const fleetClaimManagementFilterMenu = useMemo(() => {
        const activeAssetType = drawerAssetType === undefined ? filter.asset_type : drawerAssetType;
        const filteredAssemblyOptions = activeAssetType
            ? assemblyOptions.filter(item => item.asset_type === activeAssetType)
            : assemblyOptions;
        return {
            asset_type: { title: 'Asset Type', inputType: 'dropdown', options: assetType, valueKey: 'value', labelKey: 'label', multiple: false, dependentFields: ['major_assembly'] },
            major_assembly: { title: 'Assembly', inputType: 'dropdown', options: filteredAssemblyOptions, valueKey: 'value', labelKey: 'asset_name', multiple: false },
            status: { title: 'Claim Status', inputType: 'dropdown', options: claimStatusOptions, valueKey: 'value', labelKey: 'label', multiple: false },
            start_date: { title: 'Claim Start Date', inputType: 'date' },
            end_date: { title: 'Claim End Date', inputType: 'date' },
            onDependencyChange: (keyParam, value) => {
                if (keyParam === 'asset_type') setDrawerAssetType(value != null ? value : null);
            },
            onOpen: () => setDrawerAssetType(undefined),
        };
    }, [assemblyOptions, filter.asset_type, drawerAssetType]);

    useEffect(() => {
        getFleetClaimManagementList('skeletonLoader', { per_page: 50, });
        trackActivity('Page Viewed', { page_title: 'Fleet Claim Management' });
        globalGetService(getAssetDropdowmListApi()).then(response => {
            if (checkApiStatus(response)) {
                setAssemblyOptions((response?.data?.data || []).map(item => ({
                    ...item,
                    value: `[(${item.asset_id},${item.asset_type})]`,
                })));
            }
        });
    }, [])

    const sanitizeFilter = (filterObj) =>
        Object.fromEntries(Object.entries(filterObj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

    const buildApiQuery = (activeFilter) => {
        const { status, ...rest } = activeFilter;
        if (status === 5 || status === 6) {
            return { ...rest, payout_status: status };
        }
        return status !== undefined ? { ...rest, status } : rest;
    };

    const getFleetClaimManagementList = (loaderType, query = {}) => {
        loaderType === 'skeletonLoader' ? setSkeletonLoader(true) : setLoading(true);
        globalGetService(getFleetClaimManagementListApi('', query)).then(response => {
            if (checkApiStatus(response)) {
                setClaimManagementData(response?.data?.data);
                loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setLoading(false);

            }
        })
    }
    const createSortHandler = (key) => {
        let sortQuery = { sort_by: key }
        if (sortInfo.sort_by === key) {
            sortQuery = { ...sortQuery, sort: sortInfo.sort === 'asc' ? 'desc' : 'asc' }
        } else {
            sortQuery = { ...sortQuery, sort: 'asc' }
        }
        setSortInfo(sortQuery)
        getFleetClaimManagementList('', {
            ...buildApiQuery(filter),
            ...sortQuery,
            page: claimManagementData?.pagination?.current_page || 1,
            per_page: claimManagementData?.pagination?.per_page || 50,
        });
    }
    const handleExport = () => {
        setExport(true);
        globalGetService(exportFleetClaimsApi()).then((response) => {
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
            }
            setExport(false);
        })
    }
    const handleFilterChange = (updatedFilter) => {
        const sanitized = sanitizeFilter(updatedFilter);
        setFilter(sanitized);
        getFleetClaimManagementList('pageLoader', {
            ...buildApiQuery(sanitized),
            page: 1,
            per_page: claimManagementData?.pagination?.per_page || 50,
        });
    }
    return (
        <section className="active-asset-section">
            <DeploymentBannerWithOffset>
                {(bannerHeight) => {
                const listHeight = `calc(100vh - ${175 + bannerHeight}px)`;
                return (
            <div>
                <Paper elevation={0} sx={{ padding: '5px', marginBottom: '2px' }} className="active-asset-header lease-module-paper">
                    <Grid container spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Grid md={4} xs={12}>
                            {skeletonLoader ? <Skeleton variant="rounded" width={220} height={32} /> : (claimManagementData?.list?.length > 0 || Object.keys(filter).length > 0) ? <FilterComponent filter={filter} filterMenu={fleetClaimManagementFilterMenu} getResponseBack={handleFilterChange} /> : null }
                        </Grid>
                        <Grid md={8} xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <ul className="list-inline">
                                {skeletonLoader ? <li className='lease-mobile-button'> <Skeleton variant="rounded" width={90} height={32} /> </li> : claimManagementData?.list?.length > 0 ? <li className='lease-mobile-button'> <Button startIcon={isExport ? <ButtonLoader size={15} /> : <ArrowDownwardIcon />} variant="outlined" size='small' className='btn-primary' onClick={() => {
                                    handleExport(); trackActivity('Item Clicked', { page_title: `Fleet Claim Management`, page_type: 'Clicked on Export Button', })
                                }}> Export </Button>  </li> : null}
                            </ul>
                        </Grid>
                    </Grid>
                </Paper>
                <Paper className="active-asset-list lease-module-paper" elevation={0}>
                    {skeletonLoader ? <STableLoader count={6} rowCount={12} /> :
                        <div style={{ overflow: "auto", boxSizing: 'border-box', ...(claimManagementData?.list?.length > 0 ? { height: listHeight, maxHeight: 'none' } : { height: 'auto !important', maxHeight: listHeight }) }}>
                            <Table className="mui-table-format" stickyHeader>
                                <FleetClaimManagementHd sortInfo={sortInfo} createSortHandler={createSortHandler} />
                                <TableBody>
                                    {claimManagementData?.list?.length > 0 && claimManagementData?.list?.map((item, index) => {
                                        return (
                                            <ClaimManagementListing
                                                item={item}
                                                key={index}
                                                onView={() => {
                                                    if (item?.lease?.slug) {
                                                        window.open(`/lease/${item?.lease?.slug}/shop-visit/view/${item.shop_visit.slug}`, '_blank');
                                                    }
                                                }}
                                            />
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    }
                    {claimManagementData?.list?.length === 0 && <NoRecordFound />}
                    <Paper>
                    <Pagination
                        pagination={claimManagementData?.pagination}
                        onPageChange={(_, newPage) => { const pageNum = newPage + 1; getFleetClaimManagementList('pageLoader', { ...buildApiQuery(filter), page: pageNum, per_page: claimManagementData?.pagination?.per_page, }); }}
                        onRowsPerPageChange={(e) => { const perPage = parseInt(e.target.value, 10); getFleetClaimManagementList('pageLoader', { ...buildApiQuery(filter), page: 1, per_page: perPage, }); }} />
                    </Paper>
                </Paper>
            </div>
                )}}
            </DeploymentBannerWithOffset>
            {isLoading ? <PageLoader /> : null}
        </section>)
}
export default RoleBaseAccess(FleetClaimManagement, { application: 'lease_revamp', feature: 'maintenance_claims', operationType: ['R'] });